import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, Phone, ArrowRight, AlertCircle, CheckCircle, Shield, Users, Sparkles } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useForm } from '../../hooks/useApi';
import GoogleReCAPTCHA from '../Google/reCAPTCHA';
import { useGoogleAnalytics } from '../../hooks/useGoogleAnalytics';
import GoogleLogin from './GoogleLogin';
import { useNotificationHook } from '../../hooks/useNotificationHook';

const Register = () => {
  const navigate = useNavigate();
  const { register, verifyOtp, resendOtp, isAuthenticated, error: authError, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);
  const [otpStep, setOtpStep] = useState(false);
  const [otp, setOtp] = useState('');
  const [registrationData, setRegistrationData] = useState(null);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const { trackFormSubmission } = useGoogleAnalytics();
  const { notify } = useNotificationHook();

  // Form validation schema
  const validationSchema = {
    name: { required: true, minLength: 2, maxLength: 50 },
    email: { required: true, email: true },
    phone: { required: true, phone: true },
    password: { required: true, minLength: 6 },
    confirmPassword: { required: true, minLength: 6 },
  };

  // Form hook
  const {
    values,
    errors,
    touched,
    isSubmitting,
    setValue,
    setFieldTouched,
    handleSubmit,
  } = useForm({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  }, validationSchema);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Clear auth errors when component unmounts
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  // Custom validation for password confirmation
  const validatePasswordMatch = () => {
    if (values.password && values.confirmPassword && values.password !== values.confirmPassword) {
      return 'Passwords do not match';
    }
    return null;
  };

  // Handle form submission
  const onSubmit = async (formData) => {
    // Check password match
    const passwordError = validatePasswordMatch();
    if (passwordError) {
      return;
    }

    // Check terms agreement
    if (!agreeToTerms) {
      return;
    }

    // Check reCAPTCHA verification (skip if disabled)
    if (recaptchaToken !== 'DISABLED' && !recaptchaToken) {
      return;
    }

    trackFormSubmission('register_form', true);
    const { confirmPassword, ...data } = formData;
    setRegistrationData(data);
    
    const result = await register(data);
    
    if (result.success) {
      if (result.requiresOtp) {
        // Show OTP verification step
        setOtpStep(true);
        notify.success(result.message, 'OTP Sent');
      } else {
        // Direct registration success
        navigate('/dashboard', { replace: true });
      }
    }
  };

  // Handle OTP verification
  const handleOtpVerification = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      notify.error('Please enter a valid 6-digit OTP', 'Invalid OTP');
      return;
    }

    const otpData = {
      ...registrationData,
      otp: otp
    };

    const result = await verifyOtp(otpData);
    
    if (result.success) {
      notify.success('Email verified and account created successfully!', 'Success');
      navigate('/dashboard', { replace: true });
    } else {
      notify.error(result.message, 'Verification Failed');
    }
  };

  // Handle resend OTP
  const handleResendOtp = async () => {
    if (resendDisabled) return;

    setResendDisabled(true);
    setResendCountdown(60);

    const result = await resendOtp(registrationData.email);
    
    if (result.success) {
      notify.success(result.message, 'OTP Resent');
    } else {
      notify.error(result.message, 'Resend Failed');
      setResendDisabled(false);
      setResendCountdown(0);
    }

    // Start countdown
    const interval = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          setResendDisabled(false);
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setValue(name, value);
  };

  // Handle input blur
  const handleBlur = (e) => {
    const { name } = e.target;
    setFieldTouched(name, true);
  };

  // Password strength indicator
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, label: '', color: '' };
    
    let strength = 0;
    if (password.length >= 6) strength++;
    if (password.match(/[a-z]/)) strength++;
    if (password.match(/[A-Z]/)) strength++;
    if (password.match(/[0-9]/)) strength++;
    if (password.match(/[^a-zA-Z0-9]/)) strength++;

    const levels = [
      { strength: 0, label: '', color: '' },
      { strength: 1, label: 'Very Weak', color: 'bg-red-500' },
      { strength: 2, label: 'Weak', color: 'bg-orange-500' },
      { strength: 3, label: 'Fair', color: 'bg-yellow-500' },
      { strength: 4, label: 'Good', color: 'bg-blue-500' },
      { strength: 5, label: 'Strong', color: 'bg-green-500' },
    ];

    return levels[strength];
  };

  const passwordStrength = getPasswordStrength(values.password);
  const passwordMatchError = validatePasswordMatch();

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-blue-400/20 to-purple-400/20 dark:from-blue-500/10 dark:to-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-indigo-400/20 to-pink-400/20 dark:from-indigo-500/10 dark:to-pink-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-r from-blue-300/10 to-purple-300/10 dark:from-blue-400/5 dark:to-purple-400/5 rounded-full blur-3xl animate-pulse delay-500"></div>
      </div>

      {/* Back to Home Link */}
      <div className="absolute top-4 left-4 md:top-6 md:left-6 z-20">
        <Link
          to="/"
          className="inline-flex items-center px-3 py-2 md:px-4 md:py-2 text-xs md:text-sm font-medium text-gray-700 dark:text-gray-300 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md rounded-xl border border-white/20 dark:border-gray-700/50 hover:bg-white/90 dark:hover:bg-gray-800/90 transition-all duration-200 shadow-lg hover:shadow-xl"
        >
          ← Back to Home
        </Link>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
            Create Your Account
          </h2>
          <p className="mt-3 text-lg text-gray-600 dark:text-gray-300">
            Join us and start your digital journey
          </p>
          
          {/* Feature Highlights */}
          <div className="mt-8 grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Secure</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
                <Users className="w-6 h-6 text-white" />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Reliable</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 mx-auto mb-2 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400">Modern</p>
            </div>
          </div>
        </div>

        {/* Registration Form */}
        <div className="card-3d bg-white/80 dark:bg-gray-800/80 backdrop-blur-xl shadow-2xl rounded-3xl p-8 border border-white/20 dark:border-gray-700/50">
          <form className="space-y-6" onSubmit={(e) => handleSubmit(e, onSubmit)}>
            {/* Error Message */}
            {authError && (
              <div className="card-3d bg-red-50/80 dark:bg-red-900/20 backdrop-blur-md border border-red-200/50 dark:border-red-800/50 rounded-2xl p-4 shadow-lg">
                <div className="flex items-center">
                  <AlertCircle className="h-5 w-5 text-red-500 dark:text-red-400 flex-shrink-0" />
                  <div className="ml-3">
                    <p className="text-sm font-medium text-red-800 dark:text-red-300">
                      {authError}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Name Field */}
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Full Name
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <User className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" />
                </div>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`block w-full pl-12 pr-4 py-4 border-2 rounded-2xl shadow-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:bg-gray-700/80 dark:border-gray-600/50 dark:text-white backdrop-blur-md transition-all duration-300 ${
                    errors.name && touched.name
                      ? 'border-red-400 dark:border-red-500 shadow-red-100 dark:shadow-red-900/20'
                      : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500'
                  }`}
                  placeholder="Enter your full name"
                />
              </div>
              {errors.name && touched.name && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium">
                  {errors.name}
                </p>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Email Address
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`block w-full pl-12 pr-4 py-4 border-2 rounded-2xl shadow-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:bg-gray-700/80 dark:border-gray-600/50 dark:text-white backdrop-blur-md transition-all duration-300 ${
                    errors.email && touched.email
                      ? 'border-red-400 dark:border-red-500 shadow-red-100 dark:shadow-red-900/20'
                      : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500'
                  }`}
                  placeholder="Enter your email"
                />
              </div>
              {errors.email && touched.email && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium">
                  {errors.email}
                </p>
              )}
            </div>

            {/* Phone Field */}
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Phone Number
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" />
                </div>
                <input
                  id="phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  required
                  value={values.phone}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`block w-full pl-12 pr-4 py-4 border-2 rounded-2xl shadow-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:bg-gray-700/80 dark:border-gray-600/50 dark:text-white backdrop-blur-md transition-all duration-300 ${
                    errors.phone && touched.phone
                      ? 'border-red-400 dark:border-red-500 shadow-red-100 dark:shadow-red-900/20'
                      : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500'
                  }`}
                  placeholder="Enter your phone number"
                />
              </div>
              {errors.phone && touched.phone && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium">
                  {errors.phone}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Password
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`block w-full pl-12 pr-12 py-4 border-2 rounded-2xl shadow-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:bg-gray-700/80 dark:border-gray-600/50 dark:text-white backdrop-blur-md transition-all duration-300 ${
                    errors.password && touched.password
                      ? 'border-red-400 dark:border-red-500 shadow-red-100 dark:shadow-red-900/20'
                      : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500'
                  }`}
                  placeholder="Create a password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200" />
                  )}
                </button>
              </div>
              
              {/* Password Strength Indicator */}
              {values.password && (
                <div className="mt-3">
                  <div className="flex items-center space-x-3">
                    <div className="flex-1 bg-gray-200/50 dark:bg-gray-600/50 rounded-full h-2 backdrop-blur-md">
                      <div
                        className={`h-2 rounded-full transition-all duration-300 ${passwordStrength.color}`}
                        style={{ width: `${(passwordStrength.strength / 5) * 100}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      {passwordStrength.label}
                    </span>
                  </div>
                </div>
              )}
              
              {errors.password && touched.password && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium">
                  {errors.password}
                </p>
              )}
            </div>

            {/* Confirm Password Field */}
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                Confirm Password
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400 group-focus-within:text-blue-500 transition-colors duration-200" />
                </div>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={values.confirmPassword}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  className={`block w-full pl-12 pr-12 py-4 border-2 rounded-2xl shadow-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:bg-gray-700/80 dark:border-gray-600/50 dark:text-white backdrop-blur-md transition-all duration-300 ${
                    (errors.confirmPassword && touched.confirmPassword) || passwordMatchError
                      ? 'border-red-400 dark:border-red-500 shadow-red-100 dark:shadow-red-900/20'
                      : 'border-gray-200/50 dark:border-gray-600/50 hover:border-blue-300 dark:hover:border-blue-500'
                  }`}
                  placeholder="Confirm your password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors duration-200" />
                  )}
                </button>
              </div>
              
              {/* Password Match Indicator */}
              {values.confirmPassword && (
                <div className="mt-3 flex items-center">
                  {passwordMatchError ? (
                    <div className="flex items-center text-red-600 dark:text-red-400">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      <span className="text-sm font-medium">{passwordMatchError}</span>
                    </div>
                  ) : (
                    <div className="flex items-center text-green-600 dark:text-green-400">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      <span className="text-sm font-medium">Passwords match</span>
                    </div>
                  )}
                </div>
              )}
              
              {errors.confirmPassword && touched.confirmPassword && (
                <p className="mt-2 text-sm text-red-600 dark:text-red-400 font-medium">
                  {errors.confirmPassword}
                </p>
              )}
            </div>

            {/* Terms Agreement */}
            <div className="flex items-start">
              <div className="flex items-center h-5">
                <input
                  id="agree-terms"
                  name="agree-terms"
                  type="checkbox"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded-lg bg-white/80 dark:bg-gray-700/80 backdrop-blur-md"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="agree-terms" className="text-gray-700 dark:text-gray-300 font-medium">
                  I agree to the{' '}
                  <Link to="/terms-conditions" className="text-blue-600 hover:text-blue-500 dark:text-blue-400 transition-colors duration-200 hover:underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy-policy" className="text-blue-600 hover:text-blue-500 dark:text-blue-400 transition-colors duration-200 hover:underline">
                    Privacy Policy
                  </Link>
                </label>
              </div>
            </div>
            {!agreeToTerms && touched.confirmPassword && (
              <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                You must agree to the terms and conditions
              </p>
            )}

            {/* reCAPTCHA */}
            <div className="flex justify-center items-center">
              <div className="flex justify-center">
                <GoogleReCAPTCHA
                  page="register"
                  onVerify={(token) => setRecaptchaToken(token)}
                  onError={(errors) => {
                    console.error('reCAPTCHA errors:', errors);
                    setRecaptchaToken(null);
                  }}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isSubmitting || !agreeToTerms || passwordMatchError || !recaptchaToken}
                className="group relative w-full flex justify-center py-4 px-6 border border-transparent text-base font-semibold rounded-2xl text-white bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 hover:shadow-xl shadow-lg"
              >
                {isSubmitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3"></div>
                    Creating account...
                  </div>
                ) : (
                  <div className="flex items-center">
                    Create Account
                    <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-200" />
                  </div>
                )}
              </button>
            </div>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t-2 border-gray-200/50 dark:border-gray-600/50" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md text-gray-500 dark:text-gray-400 font-medium">
                  Or continue with
                </span>
              </div>
            </div>

            {/* Google Login Button */}
            <div>
              <div className="card-3d bg-white/50 dark:bg-gray-700/50 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 dark:border-gray-600/50 p-1">
                <GoogleLogin
                  onSuccess={(user) => {
                    console.log('✅ Google registration successful:', user);
                    notify.success('Account created successfully with Google!', 'Registration Success');
                    navigate('/dashboard', { replace: true });
                  }}
                  onError={(error) => {
                    console.log('❌ Google registration failed:', error);
                    notify.error('Google registration failed. Please try again.', 'Registration Error');
                  }}
                  className="w-full"
                  size="medium"
                />
              </div>
            </div>

            {/* Sign In Link */}
            <div className="text-center">
              <p className="text-base text-gray-600 dark:text-gray-300">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-semibold text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 transition-colors duration-200 hover:underline"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </form>

          {/* OTP Verification Step */}
          {otpStep && (
            <div className="mt-8 card-3d bg-blue-50/80 dark:bg-blue-900/20 backdrop-blur-md border border-blue-200/50 dark:border-blue-800/50 rounded-2xl p-6 shadow-lg">
              <div className="text-center mb-6">
                <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 mb-4 shadow-lg">
                  <Mail className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  Verify Your Email
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  We've sent a 6-digit verification code to <strong className="text-gray-900 dark:text-white">{registrationData?.email}</strong>
                </p>
              </div>

              <form onSubmit={handleOtpVerification} className="space-y-4">
                <div>
                  <label htmlFor="otp" className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                    Enter Verification Code
                  </label>
                  <input
                    id="otp"
                    type="text"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="block w-full px-4 py-4 border-2 border-gray-200/50 dark:border-gray-600/50 rounded-2xl shadow-lg placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 dark:bg-gray-700/80 dark:text-white text-center text-lg font-mono tracking-widest backdrop-blur-md transition-all duration-300"
                    placeholder="000000"
                    maxLength={6}
                    autoComplete="one-time-code"
                  />
                </div>

                <div className="flex space-x-3">
                  <button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 hover:from-blue-700 hover:via-purple-700 hover:to-indigo-700 text-white font-semibold py-3 px-4 rounded-2xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Verify & Create Account
                  </button>
                  <button
                    type="button"
                    onClick={() => setOtpStep(false)}
                    className="px-4 py-3 border-2 border-gray-200/50 dark:border-gray-600/50 text-gray-700 dark:text-gray-300 font-semibold rounded-2xl hover:bg-gray-50/80 dark:hover:bg-gray-700/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-300 backdrop-blur-md"
                  >
                    Back
                  </button>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={handleResendOtp}
                    disabled={resendDisabled}
                    className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    {resendDisabled 
                      ? `Resend in ${resendCountdown}s` 
                      : "Didn't receive the code? Resend"
                    }
                  </button>
                </div>
              </form>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            By creating an account, you agree to receive updates and promotional emails from Niswa Info Tech.
            You can unsubscribe at any time.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
